import { X } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui'
import { useROIStore } from '../store'
import { PRODUCT_BENCHMARKS, PRODUCT_CATEGORIES, formatNumber } from '../data'
import type { ProductBenchmark } from '../types'

interface AddProductModalProps {
  isOpen: boolean
  onClose: () => void
  regionId: number
}

export function AddProductModal({ isOpen, onClose, regionId }: AddProductModalProps) {
  const { addProductToStack, regions } = useROIStore()
  const [selectedProduct, setSelectedProduct] = useState<ProductBenchmark | null>(null)

  const region = regions.find((r) => r.id === regionId)
  const existingProfiles = region?.stack.map((s) => s.profile) ?? []

  // Filter out already added products
  const availableProducts = PRODUCT_BENCHMARKS.filter((p) => !existingProfiles.includes(p.profile))

  // Group by category
  const productsByCategory = PRODUCT_CATEGORIES.map((category) => ({
    ...category,
    products: availableProducts.filter((p) => p.category === category.id),
  })).filter((c) => c.products.length > 0)

  const handleAdd = () => {
    if (selectedProduct) {
      addProductToStack(regionId, selectedProduct)
      setSelectedProduct(null)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-modal w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)]">
          <h3 className="text-lg font-medium text-[var(--text-primary)]">Add to Verification Pipeline</h3>
          <button
            className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Product Selection */}
          <div>
            <label className="block text-xs text-[var(--text-muted)] mb-2">Select Product</label>
            <select
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded px-3 py-2 text-sm text-[var(--text-primary)]"
              value={selectedProduct?.profile ?? ''}
              onChange={(e) => {
                const product = PRODUCT_BENCHMARKS.find((p) => p.profile === e.target.value)
                setSelectedProduct(product ?? null)
              }}
            >
              <option value="">Choose a product...</option>
              {productsByCategory.map((category) => (
                <optgroup key={category.id} label={category.name}>
                  {category.products.map((product) => (
                    <option key={product.profile} value={product.profile}>
                      {product.description}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Selected Product Info */}
          {selectedProduct && (
            <div className="bg-[var(--bg-tertiary)] rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[var(--text-primary)]">{selectedProduct.description}</span>
                <span className="text-xs text-[var(--text-muted)] bg-[var(--bg-secondary)] px-2 py-1 rounded">
                  {selectedProduct.category}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-lg font-bold text-primary">{selectedProduct.passRate}%</div>
                  <div className="text-[10px] text-[var(--text-muted)] uppercase">Benchmark</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-success">+{selectedProduct.incrementalLift}%</div>
                  <div className="text-[10px] text-[var(--text-muted)] uppercase">Lift</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-[var(--text-primary)]">
                    {formatNumber(selectedProduct.totalVerifications)}
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] uppercase">Sample</div>
                </div>
              </div>
            </div>
          )}

          {availableProducts.length === 0 && (
            <div className="text-center py-4 text-[var(--text-muted)] text-sm">
              All products have been added to this region.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-[var(--border-color)]">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleAdd} disabled={!selectedProduct}>
            Add Product
          </Button>
        </div>
      </div>
    </div>
  )
}
